import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { authMutationError, useAuth } from '@/hooks/useAuth';
import { validatePassword, validateUsername } from '@/api/auth';
import styles from './AuthForms.module.css';

interface RegisterFormProps {
  slug: string;
  /** Wenn aus dem URL-Param `?invite=…` befüllt: schaltet das Trip-Passwort-Feld stumm. */
  inviteToken?: string;
  onSuccess: () => void;
}

export function RegisterForm({ slug, inviteToken, onSuccess }: RegisterFormProps) {
  const { registerMutation } = useAuth(slug);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [tripPassword, setTripPassword] = useState('');
  const [touched, setTouched] = useState({ username: false, password: false });

  const usernameError = touched.username ? validateUsername(username) : null;
  const passwordError = touched.password ? validatePassword(password) : null;
  const serverError = registerMutation.error ? authMutationError(registerMutation.error) : null;
  const submitDisabled = registerMutation.isPending
    || validateUsername(username) !== null
    || validatePassword(password) !== null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched({ username: true, password: true });
    if (submitDisabled) return;
    registerMutation.mutate(
      {
        username,
        password,
        displayName: displayName.trim() || undefined,
        tripPassword: inviteToken ? undefined : tripPassword,
        inviteToken: inviteToken || undefined,
      },
      { onSuccess },
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {inviteToken ? (
        <div className={styles.inviteBanner}>
          🎟️ Du bist über einen Einladungs-Link hier. Trip-Passwort ist nicht nötig.
        </div>
      ) : null}
      {serverError ? <div className={styles.serverError}>{serverError}</div> : null}
      <FormField
        label="Benutzername"
        type="text"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.currentTarget.value)}
        onBlur={() => setTouched((t) => ({ ...t, username: true }))}
        hint="3–32 Zeichen: A–Z, 0–9, _, -"
        error={usernameError}
        required
      />
      <FormField
        label="Anzeigename (optional)"
        type="text"
        autoComplete="nickname"
        value={displayName}
        onChange={(e) => setDisplayName(e.currentTarget.value)}
        hint="Wird der Crew angezeigt. Leer = Benutzername."
        maxLength={40}
      />
      <FormField
        label="Passwort"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        onBlur={() => setTouched((t) => ({ ...t, password: true }))}
        hint="Mindestens 6 Zeichen."
        error={passwordError}
        required
      />
      {!inviteToken ? (
        <FormField
          label="Trip-Passwort"
          type="password"
          autoComplete="off"
          value={tripPassword}
          onChange={(e) => setTripPassword(e.currentTarget.value)}
          hint="Vom Trip-Organisator bekommen."
        />
      ) : null}
      <Button type="submit" variant="primary" fullWidth disabled={submitDisabled}>
        {registerMutation.isPending ? 'Registriere…' : 'Account anlegen'}
      </Button>
    </form>
  );
}
